import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatPrice } from '../components/Utils/UtilsPricesFormat';
import { Helmet } from 'react-helmet';
import { buildEventBookingUrl } from '../config/billetterie';
import QrCodeSection from '../components/QrCodeSection';
import FeaturedEventBadge from '../components/Utils/FeaturedEventBadge';
import SaleStatusBadge from '../components/Utils/SaleStatusBadge';
import { attachCloseSaleDateForPublicList } from '../components/Utils/calendarEventHelpers';
import axios from 'axios';
import { apiUrl } from '../config/site';

/** Date courte type listing billetterie (ex. sam. 18 avr. 2026 · 18:00) */
function formatShortDateTime(rawDate, hour) {
    if (!rawDate) return '';
    const d = new Date(rawDate);
    const timePart = hour && typeof hour === 'string' ? hour.substring(0, 5) : '';
    if (Number.isNaN(d.getTime())) {
        return timePart ? `${rawDate} · ${timePart}` : String(rawDate);
    }
    const datePart = d.toLocaleDateString('fr-FR', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        year: 'numeric',
    });
    return timePart ? `${datePart} · ${timePart}` : datePart;
}

function PublicEventsList() {
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('Tous');
    const [sortBy, setSortBy] = useState('date');
    const [viewMode, setViewMode] = useState('grid');
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(false);
    // const [pagination, setPagination] = useState({ current_page: 1, last_page: 1 });
    useEffect(() => {
        const fetchEvents = async () => {
            setLoading(true);
            try {
                // const params = {
                //     limit: 50,
                //     page: pagination.current_page,
                // };
                // On ne filtre pas côté backend pour la recherche/catégorie
                const { data } = await axios.get(apiUrl('public/events'));
                const list = data.data.events || [];
                setEvents(list.map((ev) => attachCloseSaleDateForPublicList(ev)));
                // setPagination(data.data.pagination || { current_page: 1, last_page: 1 });
            } catch (error) {
                setEvents([]);
            }
            setLoading(false);
        };
        fetchEvents();
    }, []);

    // Filtrage et tri côté front
    const filteredEvents = useMemo(() => {
        let filtered = events.filter(event => {
            // Filtre par recherche
            const matchesSearch = (event.title?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
                (event.description?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
                (event.place?.toLowerCase() || '').includes(searchTerm.toLowerCase());
            // Filtre par catégorie
            const matchesCategory = selectedCategory === 'Tous' || event.category?.name === selectedCategory;
            return matchesSearch && matchesCategory;
        });
        // Tri
        filtered.sort((a, b) => {
            switch (sortBy) {
                case 'date':
                    return new Date(a.date) - new Date(b.date);
                case 'prix': {
                    const aMin = Math.min(...(a.pricing?.map(p => p.price) || [0]));
                    const bMin = Math.min(...(b.pricing?.map(p => p.price) || [0]));
                    return aMin - bMin;
                }
                case 'popularite':
                    return (b.features?.featured ? 1 : 0) - (a.features?.featured ? 1 : 0);
                case 'disponibilite':
                    return (b.tickets?.remaining || 0) - (a.tickets?.remaining || 0);
                default:
                    return 0;
            }
        });
        return filtered;
    }, [events, searchTerm, selectedCategory, sortBy]);

    const [categories, setCategories] = useState([]);

    useEffect(() => {
    const fetchCategories = async () => {
        try {
        const response = await axios.get(apiUrl('categories'));
        setCategories(["Tous", ...(response.data?.map(cat => cat.name) || [])]);
        } catch (error) {
        setCategories(["Tous"]);
        }
    };
    fetchCategories();
    }, []);


    return (
        <>
            <Helmet>
                <title>Événements en Côte d'Ivoire | Events Billetterie</title>
                <meta name="description" content="Découvrez les meilleurs événements en Côte d'Ivoire. Concerts, spectacles, conférences, expositions. Réservez vos billets en ligne sur Events." />
                <meta name="keywords" content="événements Côte d'Ivoire, billetterie en ligne, concerts Abidjan, spectacles, conférences, expositions, tickets événements, Events CI" />
                
                {/* Open Graph */}
                <meta property="og:title" content="Événements en Côte d'Ivoire | Events Billetterie" />
                <meta property="og:description" content="Découvrez les meilleurs événements en Côte d'Ivoire. Concerts, spectacles, conférences, expositions. Réservez vos billets en ligne." />
                <meta property="og:image" content="https://eventsbilletterie.com/assets/images/facadeEvents.png" />
                <meta property="og:url" content="https://eventsbilletterie.com/events" />
                <meta property="og:type" content="website" />
                <meta property="og:site_name" content="Events Billetterie" />
                
                {/* Twitter Card */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Événements en Côte d'Ivoire | Events Billetterie" />
                <meta name="twitter:description" content="Découvrez les meilleurs événements en Côte d'Ivoire. Réservez vos billets en ligne." />
                <meta name="twitter:image" content="https://eventsbilletterie.com/assets/images/facadeEvents.png" />
                
                {/* Canonical URL */}
                <link rel="canonical" href="https://eventsbilletterie.com/events" />
                
                {/* Données structurées JSON-LD */}
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "ItemList",
                        "name": "Événements en Côte d'Ivoire",
                        "description": "Liste des événements disponibles en Côte d'Ivoire",
                        "url": "https://eventsbilletterie.com/events",
                        "numberOfItems": filteredEvents.length,
                        "itemListElement": filteredEvents.slice(0, 10).map((event, index) => ({
                            "@type": "ListItem",
                            "position": index + 1,
                            "item": {
                                "@type": "Event",
                                "name": event.title,
                                "description": event.description || `Événement ${event.title} à ${event.place}`,
                                "startDate": event.date,
                                "endDate": event.date,
                                "eventStatus": "https://schema.org/EventScheduled",
                                "image": event.banner && !event.banner.startsWith('http') ? `data:image/png;base64,${event.banner}` : event.banner || "https://eventsbilletterie.com/assets/images/facadeEvents.png",
                                "url": buildEventBookingUrl(event.id) || `https://buy.eventsbilletterie.com/events/${event.id}/paiement`,
                                "location": {
                                    "@type": "Place",
                                    "name": event.place,
                                    "address": {
                                        "@type": "PostalAddress",
                                        "addressLocality": event.place,
                                        "addressCountry": "CI"
                                    }
                                },
                                "offers": {
                                    "@type": "Offer",
                                    "price": event.pricing && event.pricing.length > 0 ? Math.min(...event.pricing.map(p => p.price)) : 0,
                                    "priceCurrency": "XOF",
                                    "availability": event.tickets?.remaining > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
                                    "validFrom": new Date().toISOString(),
                                    "url": buildEventBookingUrl(event.id) || `https://buy.eventsbilletterie.com/events/${event.id}/paiement`
                                },
                                "organizer": {
                                    "@type": "Organization",
                                    "name": event.artist || "Events CI"
                                },
                                "performer": {
                                    "@type": "Organization",
                                    "name": event.artist || "Events CI"
                                }
                            }
                        }))
                    })}
                </script>
            </Helmet>
            
            <div className='min-h-screen' style={{ backgroundColor: '#f8fafc' }}>
            <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 overflow-hidden'>
                {/* <div className='text-center mb-6 py-8'>
                    <h1 className='text-4xl font-bold mb-4'>Découvrez nos événements</h1>
                    <p className='text-gray-600 max-w-2xl mx-auto text-lg'>Trouvez l'événement parfait parmi notre sélection d'expériences uniques</p>
                </div> */}
                <div className="bg-white rounded-lg mt-10 border border-gray-100 shadow-sm p-3 sm:p-4 mb-6">
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:gap-3">
                        <div className="relative min-w-0 flex-1">
                            <input
                                type="search"
                                enterKeyHint="search"
                                placeholder="Événement, lieu…"
                                value={searchTerm}
                                className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded-lg bg-slate-50 focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50 transition-colors"
                                onChange={(e) => setSearchTerm(e.target.value)}
                                aria-label="Rechercher un événement"
                            />
                            <svg className="absolute top-1/2 w-4 h-4 left-3 -translate-y-1/2 text-gray-400 pointer-events-none" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
                                <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <div className="flex flex-wrap items-stretch gap-2 sm:gap-3 lg:flex-nowrap lg:shrink-0">
                            <select
                                className="min-w-[8.5rem] flex-1 sm:flex-none px-2 py-2 text-sm border border-gray-300 rounded-lg bg-white cursor-pointer focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                                value={selectedCategory}
                                onChange={(e) => setSelectedCategory(e.target.value)}
                                aria-label="Catégorie"
                            >
                                {categories.map((category) => (
                                    <option key={category} value={category}>{category}</option>
                                ))}
                            </select>
                            <select
                                className="min-w-[8.5rem] flex-1 sm:flex-none px-2 py-2 text-sm border border-gray-300 rounded-lg bg-white cursor-pointer focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                                aria-label="Trier par"
                            >
                                <option value="date">Date</option>
                                <option value="prix">Prix</option>
                                <option value="popularite">Popularité</option>
                                <option value="disponibilite">Disponibilité</option>
                            </select>
                            <div className="flex rounded-lg border border-gray-200 overflow-hidden shrink-0" role="group" aria-label="Affichage">
                                <button
                                    type="button"
                                    onClick={() => setViewMode('grid')}
                                    className={`px-3 py-2 transition-colors duration-200 ${
                                        viewMode === 'grid' 
                                        ? 'bg-primary-500 text-white' 
                                        : 'bg-white text-gray-800 hover:bg-gray-100'
                                    }`}
                                    aria-pressed={viewMode === 'grid'}
                                    title="Grille"
                                >
                                    <svg className={`w-4 h-4 mx-auto ${viewMode === 'grid' ? 'text-white' : 'text-gray-800'}`} fill="currentColor" viewBox="0 0 20 20" aria-hidden>
                                        <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                                    </svg>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setViewMode('list')}
                                    className={`px-3 py-2 border-l border-gray-200 transition-colors duration-200 ${
                                        viewMode === 'list' 
                                        ? 'bg-primary-500 text-white' 
                                        : 'bg-white text-gray-800 hover:bg-gray-100'
                                    }`}
                                    aria-pressed={viewMode === 'list'}
                                    title="Liste"
                                >
                                    <svg className={`w-4 h-4 mx-auto ${viewMode === 'list' ? 'text-white' : 'text-gray-800'}`} fill="currentColor" viewBox="0 0 20 20" aria-hidden>
                                        <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                                    </svg>
                                </button>
                           
                            </div>
                            <span className="hidden sm:inline-flex items-center self-center rounded-full bg-teal-50 text-teal-800 border border-teal-100 px-2.5 py-1 text-xs font-semibold whitespace-nowrap" aria-live="polite">
                                {filteredEvents.length} résultat{filteredEvents.length > 1 ? 's' : ''}
                            </span>
                        </div>
                    </div>
                    <p className="mt-2 text-center text-xs text-slate-500 sm:hidden" aria-live="polite">
                        {filteredEvents.length} résultat{filteredEvents.length > 1 ? 's' : ''}
                    </p>
                </div>
                {loading ? (
                    <div className='flex items-center justify-center py-8'>
                        <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500'></div>
                        <span className='ml-3 text-gray-600'>Chargement des événements...</span>
                    </div>
                ) : (
                    <>
                        {viewMode === 'grid' ? (
                            <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-4 max-w-full [&>*]:min-h-0'>
                                {filteredEvents.map(event => (
                                    <EventCard key={event.id} event={event} href={buildEventBookingUrl(event.id)} />
                                ))}
                            </div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', maxWidth: '100%' }}>
                                {filteredEvents.map(event => (
                                    <EventListItem key={event.id} event={event} href={buildEventBookingUrl(event.id)} />
                                ))}
                            </div>
                        )}
                        {filteredEvents.length === 0 && (
                            <div className='text-center p-10 bg-white rounded-lg shadow-md'>
                                <div className='text-6xl mb-6 opacity-60'>🔍</div>
                                <h3 className='text-2xl font-bold text-gray-900 mb-3'>Aucun événement trouvé</h3>
                                <p className='text-gray-600 mb-6'>Essayez de modifier vos critères de recherche. <br />Restez connectés à notre newsletter pour être informés des nouveaux événements.</p>
                                <button
                                    onClick={() => {
                                        setSearchTerm('');
                                        setSelectedCategory('Tous');
                                        setSortBy('date');
                                    }}
                                    className='px-4 py-2 bg-white border border-primary-500 hover:bg-primary-500 hover:text-white text-primary-500 rounded-lg text-sm font-semibold cursor-pointer transition-colors duration-200 mr-2 sm:mr-0 mb-4 sm:mb-0'
                                >
                                    Réinitialiser les filtres
                                </button>
                                <button
                                    onClick={() => {
                                        navigate('/events-App/newsletter-inscription/r/');
                                    }}
                                    className='px-4 py-2 bg-primary-500 text-white rounded-lg text-sm font-semibold cursor-pointer transition-colors duration-200 hover:bg-primary-6000 ml-2 sm:ml-0'
                                >
                                    Je m'abonne à la newsletter
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
            <QrCodeSection />
        </div>
        </>
    );
}

/**
 * @param {'poster'|'thumb'} variant — poster: affiche entière + fond flou (grille). thumb: vignette nette type listing (lignes).
 */
function EventPosterFrame({ src, alt, className = '', variant = 'poster' }) {
    if (!src) {
        return (
            <div className={`flex items-center justify-center bg-slate-100 text-slate-500 text-xs ${className}`}>
                Aucune affiche
            </div>
        );
    }

    if (variant === 'thumb') {
        return (
            <div
                className={`relative overflow-hidden rounded-xl bg-slate-100 shadow-[0_1px_3px_rgba(0,0,0,0.08)] ring-1 ring-slate-900/[0.06] ${className}`}
            >
                <img
                    src={src}
                    alt={alt}
                    className="absolute inset-0 h-full w-full object-cover object-center"
                    loading="lazy"
                    decoding="async"
                />
            </div>
        );
    }

    return (
        <div className={`relative isolate overflow-hidden bg-gradient-to-b from-slate-100 to-slate-200/80 ${className}`}>
            <img
                src={src}
                alt=""
                aria-hidden
                className="pointer-events-none absolute inset-0 h-full w-full object-cover object-center scale-[1.08] blur-lg opacity-[0.62] saturate-[1.08]"
            />
            <div className="pointer-events-none absolute inset-0 z-[1] bg-white/[0.04]" />
            {/* Jamais en `relative` : sur Safari une <img> en flux peut imposer la hauteur intrinsèque et casser le parent flex/grid. */}
            <div className="absolute inset-0 z-10 flex items-center justify-center p-2 sm:p-2.5">
                <img
                    src={src}
                    alt={alt}
                    className="max-h-full max-w-full object-contain object-center"
                    loading="lazy"
                    decoding="async"
                />
            </div>
        </div>
    );
}

// Composant carte d'événement pour le mode grille (lien direct Laravel)
function EventCard({ event, href }) {
    const cardRef = useRef(null);
    const handleMouseEnter = () => {
        if (cardRef.current) {
            cardRef.current.style.transform = 'translateY(-4px)';
            cardRef.current.style.boxShadow = '0 10px 25px -3px rgba(0, 0, 0, 0.1)';
        }
    };
    const handleMouseLeave = () => {
        if (cardRef.current) {
            cardRef.current.style.transform = 'translateY(0)';
            cardRef.current.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
        }
    };
    // Prix minimum
    const minPrice = event.pricing && event.pricing.length > 0 ? Math.min(...event.pricing.map(p => p.price)) : 0;
    // Image (base64 ou url)
    let imageSrc = event.banner;
    if (imageSrc && !imageSrc.startsWith('http')) {
        imageSrc = `data:image/png;base64,${imageSrc}`;
    }
    const cardClassName =
        'group relative flex min-h-0 max-w-full min-w-0 flex-col overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-[0_2px_8px_rgba(0,0,0,0.06)] transition-all duration-300 hover:border-slate-300/90 hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)] cursor-pointer no-underline text-inherit';

    const cardInner = (
        <>
            {/* Hauteur fixe : WebKit gère mal aspect-ratio + max-h quand le poster n’a que des calques absolute */}
            <div className='relative z-0 h-[min(72vw,22rem)] w-full shrink-0 overflow-hidden bg-slate-100 sm:h-[20rem]'>
                <EventPosterFrame src={imageSrc} alt={event.title} variant="poster" className='absolute inset-0 h-full w-full' />
            </div>
            {/* Contenu au-dessus en pile (évite tout chevauchement visuel résiduel) */}
            <div className='relative z-[1] flex-shrink-0 bg-white p-4 pt-3'>
                <div className='flex flex-wrap items-center gap-2 mb-2'>
                    <div className='inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold uppercase tracking-wide' style={{ backgroundColor: 'rgba(91, 207, 197, 0.12)', color: '#0f766e' }}>
                        {event.category?.name}
                    </div>
                    {event.features?.featured && <FeaturedEventBadge />}
                    <SaleStatusBadge event={event} />
                </div>
                {/* Titre */}
                <h3 className='text-base sm:text-[1.05rem] font-bold mb-2 text-slate-900 leading-snug line-clamp-2'>
                    {event.title}
                </h3>
                {/* Date et heure */}
                <div className='flex items-center mb-2 text-[13px] text-slate-500'>
                    <svg className='w-4 h-4 mr-1 flex-shrink-0 text-slate-400' fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                    </svg>
                    <span className='word-wrap-break-word overflow-wrap-break-word'>
                        {formatShortDateTime(event.date, event.hour)}
                    </span>
                </div>
                {/* Lieu */}
                <div className='flex items-center mb-2 text-sm text-gray-500'>
                    <svg className='w-4 h-4 mr-1 flex-shrink-0' fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                    </svg>
                    <span className='word-wrap-break-word overflow-wrap-break-word line-height-1.4'>
                        {event.place}
                    </span>
                </div>
                {/* Prix et disponibilité */}
                <div className='flex justify-between items-center pt-3 mt-1 border-t border-slate-100 gap-2'>
                    <div className='min-w-0 flex-1'>
                        <div className='text-sm font-bold text-primary-6000 word-wrap-break-word overflow-wrap-break-word'>
                            À partir de {formatPrice(minPrice)}
                        </div>
                    </div>
                    <span className='px-3 py-1.5 bg-primary-6000 text-white rounded-lg text-xs font-semibold whitespace-nowrap flex-shrink-0 shadow-sm group-hover:bg-primary-700 transition-colors inline-block'>
                        Voir détails
                    </span>
                </div>
            </div>
        </>
    );

    const detailUrl = href || buildEventBookingUrl(event.id);

    return (
        <a
            ref={cardRef}
            href={detailUrl}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            className={cardClassName}
            data-billetterie-link={detailUrl}
        >
            {cardInner}
        </a>
    );
}

// Composant élément de liste pour le mode liste (une seule mise en page responsive, vignette taille fixe)
function EventListItem({ event, href }) {
    const itemRef = useRef(null);
    const handleMouseEnter = () => {
        if (itemRef.current) {
            itemRef.current.style.transform = 'translateY(-2px)';
            itemRef.current.style.boxShadow = '0 8px 16px rgba(0, 0, 0, 0.1)';
        }
    };
    const handleMouseLeave = () => {
        if (itemRef.current) {
            itemRef.current.style.transform = 'translateY(0)';
            itemRef.current.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
        }
    };
    const minPrice = event.pricing && event.pricing.length > 0 ? Math.min(...event.pricing.map(p => p.price)) : 0;
    let imageSrc = event.banner;
    if (imageSrc && !imageSrc.startsWith('http')) {
        imageSrc = `data:image/png;base64,${imageSrc}`;
    }

    const itemClassName =
        'group mb-3 flex max-w-full min-w-0 cursor-pointer items-start gap-3 rounded-2xl border border-slate-200/90 bg-white p-3 shadow-[0_2px_8px_rgba(0,0,0,0.06)] transition-all duration-300 hover:shadow-[0_8px_20px_rgba(0,0,0,0.07)] sm:gap-4 sm:p-4 no-underline text-inherit';

    const itemInner = (
        <>
            {/* Hauteur / largeur en px (rem) — plus fiable que aspect-ratio sur Safari */}
            <div className='relative h-28 w-[5.25rem] shrink-0 overflow-hidden rounded-xl sm:h-[7.5rem] sm:w-28'>
                <EventPosterFrame src={imageSrc} alt={event.title} variant="thumb" className='absolute inset-0 h-full w-full' />
            </div>

            <div className='flex min-w-0 flex-1 flex-col gap-2 pt-0.5 sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:pt-0'>
                <div className='min-w-0 flex-1'>
                    <div className='mb-1.5 flex flex-wrap items-center gap-2'>
                        <div
                            className='inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide'
                            style={{ backgroundColor: 'rgba(91, 207, 197, 0.12)', color: '#0f766e' }}
                        >
                            {event.category?.name}
                        </div>
                        {event.features?.featured && <FeaturedEventBadge />}
                        <SaleStatusBadge event={event} />
                    </div>
                    <h3 className='mb-1 line-clamp-2 text-base font-bold leading-snug text-slate-900'>{event.title}</h3>
                    <div className='flex flex-col gap-1 text-[13px] text-slate-500'>
                        <div className='flex min-w-0 items-center'>
                            <svg className='mr-1 h-3.5 w-3.5 flex-shrink-0 text-slate-400' fill="currentColor" viewBox="0 0 20 20">
                                <path
                                    fillRule="evenodd"
                                    d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                                    clipRule="evenodd"
                                />
                            </svg>
                            <span className='min-w-0 break-words'>{formatShortDateTime(event.date, event.hour)}</span>
                        </div>
                        <div className='flex min-w-0 items-start'>
                            <svg className='mr-1 mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-slate-400' fill="currentColor" viewBox="0 0 20 20">
                                <path
                                    fillRule="evenodd"
                                    d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                                    clipRule="evenodd"
                                />
                            </svg>
                            <span className='line-clamp-2 min-w-0 break-words'>{event.place}</span>
                        </div>
                    </div>
                </div>

                <div className='flex shrink-0 flex-col items-center justify-between gap-2 border-t border-slate-100 pt-2 sm:w-auto sm:flex-col sm:items-end sm:justify-center sm:border-0 sm:pt-0'>
                    <div className='text-sm font-bold text-primary-6000'>À partir de {formatPrice(minPrice)}</div>
                    <span className='rounded-lg bg-primary-6000 px-3 py-1.5 text-center text-xs font-semibold whitespace-nowrap text-white shadow-sm sm:min-w-[6.5rem] inline-block'>
                        Voir détails
                    </span>
                </div>
            </div>
        </>
    );

    const detailUrl = href || buildEventBookingUrl(event.id);

    return (
        <a
            ref={itemRef}
            href={detailUrl}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            className={itemClassName}
            data-billetterie-link={detailUrl}
        >
            {itemInner}
        </a>
    );
}

export default PublicEventsList; 